import React, { useEffect, useMemo, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  CalendarDays,
  List,
  Plus,
  Trophy,
  Dumbbell,
  Filter,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Clock,
  X,
  Save,
  Loader2,
  AlertTriangle,
  Trash2,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type EventType = "match" | "training";

type TeamEvent = {
  id: string;
  type: EventType;
  startAt: string;
  endAt?: string;
  location?: string;
  opponent?: string;
  notes?: string;
  score?: string;
  victory?: boolean | null;
  createdByUserId?: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDayLabel(d: Date) {
  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" });
}


const CAN_ADD_TEAM_ROLES = ["owner", "coach", "assistant"];
const CAN_ADD_CLUB_ROLES = ["dirigeant", "cadre_technique"];

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TeamCalendar() {
  const { teamId, clubId } = useParams<{ clubId: string; teamId: string }>();

  // ── Data state ──
  const [events, setEvents] = useState<TeamEvent[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [teamRole, setTeamRole] = useState<string | null>(null);
  const [clubRole, setClubRole] = useState<string | null>(null);

  // ── UI state ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState<"list" | "calendar">("list");
  const [filter, setFilter] = useState<"all" | EventType>("all");
  const [monthCursor, setMonthCursor] = useState<Date>(startOfMonth(new Date()));
  const [deleting, setDeleting] = useState<string | null>(null);

  // ── Create form state ──
  const [openCreate, setOpenCreate] = useState(false);
  const [newType, setNewType] = useState<EventType>("training");
  const [newOpponent, setNewOpponent] = useState("");
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [newLocation, setNewLocation] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // ── Derived ──
  const monthStart = useMemo(() => startOfMonth(monthCursor), [monthCursor]);
  const monthEnd = useMemo(() => endOfMonth(monthCursor), [monthCursor]);
  const canAdd =
    (!!teamRole && CAN_ADD_TEAM_ROLES.includes(teamRole)) ||
    (!!clubRole && CAN_ADD_CLUB_ROLES.includes(clubRole));

  // ── Load ──
  useEffect(() => {
    if (!teamId || !clubId) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const { data: { user }, error: authErr } = await supabase.auth.getUser();
        if (authErr) throw authErr;
        if (!user) throw new Error("Utilisateur non connecté.");

        const [matchesRes, trainingsRes, teamRoleRes, clubRoleRes] = await Promise.all([
          supabase
            .from("matches")
            .select("id, date, opponent, location, score, victory, user_id")
            .eq("team_id", teamId)
            .order("date", { ascending: true }),
          supabase
            .from("trainings")
            .select("id, date, location, notes, end_time, user_id")
            .eq("team_id", teamId)
            .order("date", { ascending: true }),
          supabase
            .from("team_members")
            .select("role")
            .eq("team_id", teamId)
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase
            .from("club_members")
            .select("role")
            .eq("club_id", clubId)
            .eq("user_id", user.id)
            .maybeSingle(),
        ]);

        if (matchesRes.error) throw matchesRes.error;
        if (trainingsRes.error) throw trainingsRes.error;

        if (!cancelled) {
          setCurrentUserId(user.id);
          setTeamRole(teamRoleRes.data?.role ?? null);
          setClubRole(clubRoleRes.data?.role ?? null);

          const matchEvents: TeamEvent[] = (matchesRes.data || []).map((m: any) => ({
            id: m.id,
            type: "match" as const,
            startAt: m.date,
            opponent: m.opponent ?? undefined,
            location: m.location ?? undefined,
            score: m.score ?? undefined,
            victory: m.victory ?? null,
            createdByUserId: m.user_id ?? undefined,
          }));

          const trainingEvents: TeamEvent[] = (trainingsRes.data || []).map((t: any) => ({
            id: t.id,
            type: "training" as const,
            startAt: t.date,
            endAt: t.end_time ?? undefined,
            location: t.location ?? undefined,
            notes: t.notes ?? undefined,
            createdByUserId: t.user_id ?? undefined,
          }));

          setEvents([...matchEvents, ...trainingEvents]);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Impossible de charger le calendrier.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [teamId, clubId]);

  // ── Submit new event ──
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!newStart || !teamId || !currentUserId) return;

    setSubmitting(true);
    setSubmitError("");

    try {
      if (newType === "training") {
        const { data, error: insertErr } = await supabase
          .from("trainings")
          .insert({
            team_id: teamId,
            user_id: currentUserId,
            date: new Date(newStart).toISOString(),
            end_time: newEnd ? new Date(newEnd).toISOString() : null,
            location: newLocation.trim() || null,
            notes: newNotes.trim() || null,
          })
          .select("id, date, location, notes, end_time, user_id")
          .single();

        if (insertErr) throw insertErr;

        setEvents((prev) => [
          ...prev,
          {
            id: data.id,
            type: "training",
            startAt: data.date,
            endAt: data.end_time ?? undefined,
            location: data.location ?? undefined,
            notes: data.notes ?? undefined,
            createdByUserId: data.user_id ?? undefined,
          },
        ]);
      } else {
        const { data, error: insertErr } = await supabase
          .from("matches")
          .insert({
            team_id: teamId,
            user_id: currentUserId,
            date: new Date(newStart).toISOString(),
            opponent: newOpponent.trim() || null,
            location: newLocation.trim() || null,
          })
          .select("id, date, opponent, location, score, victory, user_id")
          .single();

        if (insertErr) throw insertErr;

        setEvents((prev) => [
          ...prev,
          {
            id: data.id,
            type: "match",
            startAt: data.date,
            opponent: data.opponent ?? undefined,
            location: data.location ?? undefined,
            score: data.score ?? undefined,
            victory: data.victory ?? null,
            createdByUserId: data.user_id ?? undefined,
          },
        ]);
      }

      setOpenCreate(false);
      resetForm();
    } catch (err: any) {
      setSubmitError(err?.message || "Erreur lors de la création.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Delete event ──
  async function handleDelete(ev: TeamEvent) {
    if (!window.confirm("Supprimer cet événement ?")) return;
    setDeleting(ev.id);

    const table = ev.type === "match" ? "matches" : "trainings";
    const { error: delErr } = await supabase.from(table).delete().eq("id", ev.id);
    setDeleting(null);

    if (delErr) { alert(delErr.message); return; }
    setEvents((prev) => prev.filter((e) => e.id !== ev.id));
  }

  function resetForm() {
    setNewType("training");
    setNewOpponent("");
    setNewStart("");
    setNewEnd("");
    setNewLocation("");
    setNewNotes("");
    setSubmitError("");
  }

  // ── Filtered & sorted events ──
  const filteredEvents = useMemo(() => {
    const sorted = [...events].sort(
      (a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime()
    );
    if (filter === "all") return sorted;
    return sorted.filter((e) => e.type === filter);
  }, [events, filter]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, TeamEvent[]>();
    for (const e of filteredEvents) {
      const d = new Date(e.startAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
    }
    return map;
  }, [filteredEvents]);

  const monthDays = useMemo(() => {
    const first = new Date(monthStart);
    const firstDow = (first.getDay() + 6) % 7;
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - firstDow);

    const last = new Date(monthEnd);
    const lastDow = (last.getDay() + 6) % 7;
    const gridEnd = new Date(last);
    gridEnd.setDate(last.getDate() + (6 - lastDow));

    const days: Date[] = [];
    const cur = new Date(gridStart);
    while (cur <= gridEnd) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  }, [monthStart, monthEnd]);

  // ── Render guards ──
  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement du calendrier…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300">
        <div className="inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col items-center text-center gap-3 sm:flex-row sm:items-center sm:justify-between sm:text-left">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Calendrier équipe</div>
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Matchs & entraînements
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {events.length} événement{events.length !== 1 ? "s" : ""} au total.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
            <button
              type="button"
              onClick={() => setView("list")}
              className={cx(
                "px-3 py-2 text-sm inline-flex items-center gap-2",
                view === "list"
                  ? "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                  : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              )}
            >
              <List className="h-4 w-4" />
              Liste
            </button>
            <button
              type="button"
              onClick={() => setView("calendar")}
              className={cx(
                "px-3 py-2 text-sm inline-flex items-center gap-2 border-l border-gray-200 dark:border-gray-800",
                view === "calendar"
                  ? "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100"
                  : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              )}
            >
              <CalendarDays className="h-4 w-4" />
              Calendrier
            </button>
          </div>

          {canAdd && (
            <button
              type="button"
              onClick={() => setOpenCreate(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-sm"
            >
              <Plus className="h-4 w-4" />
              Ajouter
            </button>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <Filter className="h-4 w-4 text-gray-500" />
            Filtrer
          </div>
          <div className="flex flex-wrap gap-2">
            {(["all", "match", "training"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={cx(
                  "px-3 py-2 rounded-xl text-sm border",
                  filter === f
                    ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                    : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
              >
                {f === "all" ? "Tout" : f === "match" ? "Matchs" : "Entraînements"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* LIST VIEW */}
      {view === "list" && (
        <div className="space-y-4">
          {filteredEvents.length === 0 && (
            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500">
              <CalendarDays className="h-8 w-8" />
              <div className="text-sm">Aucun événement pour ce filtre.</div>
            </div>
          )}

          {Array.from(eventsByDay.entries()).map(([key, dayEvents]) => {
            const [y, m, d] = key.split("-").map(Number);
            const day = new Date(y, m, d);
            const isToday = sameDay(day, new Date());

            return (
              <div key={key} className="space-y-2">
                <div className={cx(
                  "text-sm font-semibold",
                  isToday ? "text-indigo-700 dark:text-indigo-300" : "text-gray-900 dark:text-gray-100"
                )}>
                  {formatDayLabel(day)}{isToday && " — Aujourd'hui"}
                </div>

                {dayEvents.map((ev) => {
                  const isOwn = ev.createdByUserId === currentUserId;
                  const canDelete = canAdd || isOwn;

                  const cardContent = (
                    <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 hover:border-gray-300 dark:hover:border-gray-700 transition">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={cx(
                              "inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full",
                              ev.type === "match"
                                ? "bg-blue-100 text-blue-900 dark:bg-blue-900/20 dark:text-blue-200"
                                : "bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-200"
                            )}>
                              {ev.type === "match"
                                ? <><Trophy className="h-3.5 w-3.5" />Match</>
                                : <><Dumbbell className="h-3.5 w-3.5" />Entraînement</>}
                            </span>

                            <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {ev.type === "match" && ev.opponent
                                ? `vs ${ev.opponent}`
                                : ev.type === "match" ? "Match" : "Entraînement"}
                            </div>

                            {ev.type === "match" && ev.score && (
                              <span className="text-sm font-mono text-gray-700 dark:text-gray-300">
                                {ev.score}
                              </span>
                            )}
                            {ev.type === "match" && ev.victory !== null && ev.victory !== undefined && (
                              <span className={cx(
                                "text-xs px-2 py-0.5 rounded-full",
                                ev.victory
                                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                  : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                              )}>
                                {ev.victory ? "Victoire" : "Défaite"}
                              </span>
                            )}
                          </div>

                          <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {formatDateTime(ev.startAt)}
                              {ev.endAt
                                ? ` → ${new Date(ev.endAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`
                                : ""}
                            </span>
                            {ev.location && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                {ev.location}
                              </span>
                            )}
                          </div>

                          {ev.notes && (
                            <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {ev.notes}
                            </div>
                          )}
                        </div>

                        {canDelete && (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(ev); }}
                            disabled={deleting === ev.id}
                            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-40"
                            aria-label="Supprimer"
                          >
                            {deleting === ev.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Trash2 className="h-4 w-4" />}
                          </button>
                        )}
                      </div>
                    </div>
                  );

                  if (ev.type === "training") {
                    return (
                      <NavLink
                        key={ev.id}
                        to={`/club/${clubId}/trainings/${ev.id}`}
                        className="block"
                      >
                        {cardContent}
                      </NavLink>
                    );
                  }

                  return (
                    <NavLink
                      key={ev.id}
                      to={`/club/${clubId}/team/${teamId}/calendar/match/${ev.id}`}
                      className="block"
                    >
                      {cardContent}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* CALENDAR VIEW */}
      {view === "calendar" && (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
          <div className="flex items-center justify-between gap-3 mb-4">
            <button
              type="button"
              onClick={() => setMonthCursor((d) => addMonths(d, -1))}
              className="inline-flex items-center justify-center h-9 w-9 rounded-xl border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
              aria-label="Mois précédent"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">
              {monthStart.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
            </div>

            <button
              type="button"
              onClick={() => setMonthCursor((d) => addMonths(d, 1))}
              className="inline-flex items-center justify-center h-9 w-9 rounded-xl border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
              aria-label="Mois suivant"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-xs text-gray-500 dark:text-gray-400 mb-2">
            {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((w) => (
              <div key={w} className="px-1 text-center">{w}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {monthDays.map((day) => {
              const inMonth = day.getMonth() === monthStart.getMonth();
              const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
              const dayEvents = eventsByDay.get(key) ?? [];
              const matchCount = dayEvents.filter((e) => e.type === "match").length;
              const trainingCount = dayEvents.filter((e) => e.type === "training").length;
              const firstTraining = dayEvents.find((e) => e.type === "training");

              return (
                <div
                  key={key}
                  className={cx(
                    "min-h-[80px] rounded-xl border p-1.5",
                    inMonth
                      ? "border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900"
                      : "border-gray-100 dark:border-gray-900 bg-gray-50/40 dark:bg-gray-950/30",
                    sameDay(day, new Date()) && "ring-2 ring-indigo-300 dark:ring-indigo-700"
                  )}
                >
                  <div className={cx(
                    "text-xs mb-1",
                    inMonth ? "text-gray-900 dark:text-gray-100" : "text-gray-400",
                    sameDay(day, new Date()) && "font-bold text-indigo-600 dark:text-indigo-400"
                  )}>
                    {day.getDate()}
                  </div>

                  {matchCount > 0 && (
                    <div className="text-[10px] px-1.5 py-0.5 rounded-md bg-blue-100 text-blue-900 dark:bg-blue-900/20 dark:text-blue-200 mb-0.5 truncate">
                      {matchCount} match{matchCount > 1 ? "s" : ""}
                    </div>
                  )}

                  {trainingCount > 0 && firstTraining && (
                    <NavLink
                      to={`/club/${clubId}/trainings/${firstTraining.id}`}
                      className="block text-[10px] px-1.5 py-0.5 rounded-md bg-green-100 text-green-900 dark:bg-green-900/20 dark:text-green-200 hover:underline truncate"
                    >
                      {trainingCount} entraînement{trainingCount > 1 ? "s" : ""}
                    </NavLink>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create modal */}
      {openCreate && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setOpenCreate(false); resetForm(); }} />
          <div className="absolute inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center p-3">
            <div className="w-full sm:max-w-2xl rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="font-semibold text-gray-900 dark:text-gray-100">Ajouter un événement</div>
                <button type="button" onClick={() => { setOpenCreate(false); resetForm(); }}
                  className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:underline">
                  <X className="h-4 w-4" />Fermer
                </button>
              </div>

              {submitError && (
                <div className="mb-3 rounded-xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Type selector */}
                <div className="grid grid-cols-2 gap-2">
                  {(["training", "match"] as EventType[]).map((t) => (
                    <button key={t} type="button" onClick={() => setNewType(t)}
                      className={cx(
                        "px-3 py-2 rounded-xl text-sm border inline-flex items-center justify-center gap-2",
                        newType === t
                          ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                          : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                      )}>
                      {t === "training"
                        ? <><Dumbbell className="h-4 w-4" />Entraînement</>
                        : <><Trophy className="h-4 w-4" />Match</>}
                    </button>
                  ))}
                </div>

                {newType === "match" && (
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Adversaire</label>
                    <input value={newOpponent} onChange={(e) => setNewOpponent(e.target.value)}
                      placeholder="Ex : Roanne"
                      className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700" />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Début *</label>
                    <input type="datetime-local" value={newStart} onChange={(e) => setNewStart(e.target.value)} required
                      className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Fin (optionnel)</label>
                    <input type="datetime-local" value={newEnd} onChange={(e) => setNewEnd(e.target.value)}
                      className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700" />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Lieu</label>
                  <input value={newLocation} onChange={(e) => setNewLocation(e.target.value)}
                    placeholder="Gymnase / Salle / Extérieur…"
                    className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700" />
                </div>

                {newType === "training" && (
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Notes</label>
                    <textarea value={newNotes} onChange={(e) => setNewNotes(e.target.value)} rows={3}
                      placeholder="RDV / consignes / matériel…"
                      className="mt-1 w-full px-3 py-2 rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700" />
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button type="button" onClick={() => { setOpenCreate(false); resetForm(); }}
                    className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800">
                    Annuler
                  </button>
                  <button type="submit" disabled={submitting}
                    className={cx(
                      "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm",
                      "bg-gray-900 text-white hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100",
                      submitting && "opacity-60 cursor-not-allowed"
                    )}>
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {submitting ? "Enregistrement…" : "Enregistrer"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
